import type { ContestScoringFormat, ContestStandingsResponse, StandingsRow } from "../types";

/**
 * What the redrawn scoreboard says about a board, derived from the server's
 * payload and never from a fold of its own (`docs/29` §6.1).
 *
 * Every function here reads either the field-wide numbers the server puts on
 * `problems[]` (solved, tried, pending) or the top of page one, which is always
 * the leader. Nothing here looks at "the rows on this page" to say something
 * about the whole field, because on a board of several pages that sentence
 * would be wrong for exactly the contests big enough to want it.
 */

type Problem = ContestStandingsResponse["problems"][number];

/**
 * The backend's `ICPC_PENALTY_MINUTES_PER_WRONG` (`contest-standings.ts`). Used
 * only in the sentence under the board that explains the ranking; the ranking
 * itself is the server's.
 */
export const ICPC_PENALTY_MINUTES_PER_WRONG = 20;

/** The name a row leads with: the handle when the server sent one, else the name. */
export function rowLabel(row: Pick<StandingsRow, "name" | "identity">): string {
  return row.identity?.username ?? row.name ?? "-";
}

/**
 * The problem the fewest entries solved, with how many tried it. Ties go to the
 * one more entries tried (a problem nobody dared touch is not the hardest, one
 * many failed is). `null` when there is one problem, when the server sent no
 * counts (an older backend), or when nobody has tried anything yet.
 */
export function hardestProblem(problems: Problem[]): { letter: string; solved: number; tried: number } | null {
  if (problems.length < 2) return null;
  let best: { letter: string; solved: number; tried: number } | null = null;
  for (const problem of problems) {
    if (problem.solvedCount === undefined || problem.attemptedCount === undefined) continue;
    if (problem.attemptedCount <= 0) continue;
    const candidate = { letter: problem.letter, solved: problem.solvedCount, tried: problem.attemptedCount };
    if (!best || candidate.solved < best.solved || (candidate.solved === best.solved && candidate.tried > best.tried)) {
      best = candidate;
    }
  }
  return best;
}

/** Solves across the field, from the columns' own counts; `null` without them. */
export function solveTotal(problems: Problem[]): number | null {
  let total = 0;
  let seen = false;
  for (const problem of problems) {
    if (problem.solvedCount === undefined) continue;
    seen = true;
    total += problem.solvedCount;
  }
  return seen ? total : null;
}

/**
 * Entries with a submission waiting for its verdict, across every problem;
 * `null` when the server sent no `pendingCount` at all (an older backend), so
 * the card can fall back instead of claiming "0 being judged".
 */
export function pendingTotal(problems: Problem[]): number | null {
  let total = 0;
  let seen = false;
  for (const problem of problems) {
    if (problem.pendingCount === undefined) continue;
    seen = true;
    total += problem.pendingCount;
  }
  return seen ? total : null;
}

function isZero(row: StandingsRow, format: ContestScoringFormat): boolean {
  return format === "icpc" ? row.solved === 0 : row.score === 0;
}

/**
 * The leader is the first row of page one; anywhere else the page says nothing
 * about them. Somebody who has scored nothing leads nothing, so a board where
 * nobody has solved a problem yet has no leader to name (a "Winner" with 0
 * solved, seen on a real contest the day this shipped).
 */
export function leaderOf(
  data: Pick<ContestStandingsResponse, "rows" | "page">,
  format: ContestScoringFormat,
  filtered: boolean,
): StandingsRow | null {
  if (filtered || data.page !== 1) return null;
  // `EX-DIV01`: an unofficial row shown above the leader leads nothing; the
  // first official row does.
  const first = data.rows.find((row) => row.rank !== null);
  return first && first.rank === 1 && !isZero(first, format) ? first : null;
}

/**
 * The rows at the bottom of a page that have solved nothing, so the board can
 * say once why so many of them wear the same rank instead of leaving a column
 * of 9s to be puzzled over. `null` when there are none, or when every row on the
 * page is such a row (then there is nothing to set them apart from).
 */
export function zeroGroup(
  rows: StandingsRow[],
  format: ContestScoringFormat,
): { firstIndex: number; count: number; rank: number; shareRank: boolean } | null {
  const firstIndex = rows.findIndex((row) => isZero(row, format));
  if (firstIndex <= 0) return null;
  const group = rows.slice(firstIndex);
  if (!group.every((row) => isZero(row, format))) return null;
  // `EX-DIV01`: the shared rank is the official rows'; an unofficial row has none.
  const official = group.filter((row) => row.rank !== null);
  const rank = official[0]?.rank ?? null;
  if (rank === null) return null;
  return { firstIndex, count: official.length, rank, shareRank: official.every((row) => row.rank === rank) };
}

/**
 * `EX-SCB03`: how full the ring around a problem's letter is, the share of the
 * entries that tried it and solved it, from the column's own counts (so a
 * frozen board's ring never shows a solve the board withholds). 0 when the
 * counts are absent or nobody has tried, never more than 1.
 */
export function ringFraction(problem: Pick<Problem, "solvedCount" | "attemptedCount">): number {
  const solved = problem.solvedCount ?? 0;
  const tried = problem.attemptedCount ?? 0;
  if (!(tried > 0) || !(solved > 0)) return 0;
  return Math.min(1, solved / tried);
}

const BALLOON_HEX = /^#[0-9a-fA-F]{6}$/;

/** A balloon is drawn from a colour the server stored; only a plain hex is ever put in a style. */
export function balloonHex(color: { hex: string; name: string } | null | undefined): string | null {
  return color && BALLOON_HEX.test(color.hex) ? color.hex : null;
}
